import { WSClient, ZapReadRecord } from '../types';

import { TransferClientError, TransferWebSocketError } from '../../../errors';

export enum MESSAGE_TYPE {
  BATCH_READ = 'BATCH_READ',
  END_READ = 'END_READ',
  READ_ERROR = 'READ_ERROR',
}

export type BatchReadMessage = {
  type: MESSAGE_TYPE.BATCH_READ;
  results: ZapReadRecord[];
  bulk_id: string;
  paid_plan?: boolean;
};

export type EndReadMessage = {
  type: MESSAGE_TYPE.END_READ;
  bulk_id: string;
  paid_plan?: boolean;
};

export type ReadErrorMessage = {
  type: MESSAGE_TYPE.READ_ERROR;
  bulk_id: string;
  error: string;
};

export type ReadRequestPayload = {
  accountId: string;
  action: string;
  selectedAPI: string;
  params: { [key: string]: any };
  typeOf: string;
  authId: number;
  zapId: string;
};

type GetZapReadRecordOptions = {
  readParams: ReadRequestPayload;
  onOpen: () => void;
  onReceiveRecords: (data: BatchReadMessage) => void;
  onReadComplete: (data: EndReadMessage) => void;
  onReadFailed: (data: TransferClientError) => void;
  onDone: () => void;
  onError: (error: TransferClientError | TransferWebSocketError) => void;
};

const getZapReadRecords = async (
  client: WSClient,
  {
    readParams,
    onOpen,
    onReceiveRecords,
    onReadComplete,
    onReadFailed,
    onDone,
    onError,
  }: GetZapReadRecordOptions
): Promise<void> => {
  // Generate the client
  const ws = await client('v1/ws/records/');

  /** Locally scoped wrapper to ensure we only call 'onDone' once. */
  let onDoneCalled = false;
  const onDoneOnce = () => {
    if (!onDoneCalled) {
      onDone();
      onDoneCalled = true;
      return;
    }
  };

  /** Handler to notify when the connection has been opened */
  ws.onopen = () => {
    ws.send(JSON.stringify({ read_config: readParams }));
    onOpen();
  };

  /** Handler called whenever there is a new message over the connection. */
  ws.onmessage = (ev: MessageEvent) => {
    if (typeof ev.data === 'string') {
      try {
        const parsed = JSON.parse(ev.data);
        switch (parsed.type) {
          case MESSAGE_TYPE.BATCH_READ:
            onReceiveRecords(parsed);
            break;
          case MESSAGE_TYPE.READ_ERROR:
            onReadFailed(new TransferClientError(parsed.error as string));
            break;
          case MESSAGE_TYPE.END_READ:
            onReadComplete(parsed);
            break;
          default:
            throw new Error('Invalid WebSocket data');
        }
      } catch (e) {
        onError(new TransferClientError((e as Error)?.message));
      }
    }
  };

  ws.onclose = (ev: CloseEvent) => {
    if (ev.code === 1000) {
      onDoneOnce();
    } else {
      onError(new TransferWebSocketError(readParams, ev));
    }
  };

  ws.onerror = (ev) => {
    onError(new TransferWebSocketError(readParams, ev));
  };
};

export default getZapReadRecords;
